/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import {
	Box,
	Stack,
	Text,
	Image,
	Container,
} from '@mantine/core';

// Components
import { Button, Searchbar } from '@components/atoms';
import { SiteAccordion } from './SiteAccordion';
import {
	ConversionFactorData,
	MeterpointData,
	SiteItem,
} from './SiteItem';

// Styles
import classes from './Sites.module.css';

// Icons
import { IconCirclePlus } from '@tabler/icons-react';

// I18n
import { useTranslation } from 'react-i18next';

// Mocks
import { TSite } from '@global-types/index';
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';
import useStore from '@store/index';

/* -------------------------------------------------------------------------- */
/*                                    Typs                                    */
/* -------------------------------------------------------------------------- */
type SitesProps = {
	data: TSiteCustom[];
	handlesearchsite: (value: string) => void;
	projectId: string | undefined;
	producerId: string | undefined;
};

export type TSiteCustom = {
	id: string;
	name: string;
	countryCode: string;
	place: string;
	unit: string;
	site: TSite;
	meterPoints: MeterpointData[];
	conversionFactors: ConversionFactorData[];
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const Sites = (props: SitesProps) => {
	const { t } = useTranslation();
	const { setOpenDrawerSiteForm } = useStore();

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.wrapper}>
			<Box className={classes.header}>
				<Text className={classes.title}>Sites</Text>
				<Container
					className={classes.containerSearchInput}
				>
					<Searchbar
						size={'md'}
						placeholder={t('placeholders.searchSite')}
						onChange={(event) =>
							props.handlesearchsite(
								event.currentTarget.value
							)
						}
					/>
				</Container>

				<Button
					leftSection={<IconCirclePlus size={23} />}
					size={'md'}
					onClick={() => {
						setOpenDrawerSiteForm(true);
					}}
				>
					{t('global.addNewSite')}
				</Button>
			</Box>

			<Box className={classes.content}>
				{Array.isArray(props.data) &&
				props.data.length > 0 ? (
					props.data.map((_site: TSiteCustom) => (
						<SiteAccordion
							key={_site.id}
							label={_site.name}
							panel={
								<SiteItem
									site={_site}
									key={_site.id}
									conversionFactors={
										_site.conversionFactors
									}
									meterpoints={_site.meterPoints}
									projectId={props.projectId}
									producerId={props.producerId}
								/>
							}
							handleEdit={() => {}}
							handleDelete={() => {}}
						/>
					))
				) : (
					<Stack
						justify="center"
						align="center"
						pt={'15px'}
						className={classes.containerNoDatas}
					>
						<Image
							src={NoRecordsIcon}
							className={classes.iconNoRecords}
						/>
						<Text
							className={classes.descriptionNoRecords}
						>
							{t('global.noSitesAdded')}
						</Text>
					</Stack>
				)}
			</Box>
		</Box>
	);
};
